// Step 3's "Where {site} is in the story": the six chapters of Step 1 as buttons, and under
// them, for the one opened, each of its steps on this building: its state, who owns it,
// its date and what came of it. Read only: every word is the page's own, written from the
// record (overrides applied) the way Step 2's hover cards and Step 3's cards write it.
// docs/Y2026-Q4/design-amazon-overview-qa-fixes.md s3 item 1.
import { useState } from "react";

import type { AmazonBuilding, AmazonNeed } from "../../api/hooks/useAmazonOverview";
import { Chevron } from "../../components/Chevron";
import type { StoryStep } from "./blueprint";
import { capitalised, repeatsBold, shownCell, shownLine } from "./cells";
import { CHAPTERS } from "./chapters";
import { day, daysBetween, formatDate } from "./dates";
import type { Target } from "./editContext";
import { Editable } from "./editing";
import { HeldItems } from "./HeldItems";
import { heldItems, waitingText } from "./needs";
import type { Overrides } from "./overrides";
import { PartyChip } from "./PartyChip";
import { chapterProgress, isSettled, shownState } from "./progress";
import { lateInputs } from "./schedule";
import styles from "./amazonOverview.module.css";

type RowState = "done" | "open" | "blocked" | "late" | "na" | "notStarted" | "notYet";

const ROW_LABEL: Record<RowState, string> = {
  done: "Done",
  open: "In progress",
  blocked: "Blocked",
  late: "Late",
  na: "Not applicable",
  notStarted: "Not started",
  notYet: "Not happened yet",
};

const ROW_CLASS: Record<RowState, string> = {
  done: styles.rowDone,
  open: styles.rowOpen,
  blocked: styles.rowBlocked,
  late: styles.rowBlocked,
  na: styles.rowNone,
  notStarted: styles.rowNone,
  notYet: styles.rowNone,
};

const TILE_CLASS = {
  done: styles.progDone,
  now: styles.progNow,
  blocked: styles.progBlocked,
  next: styles.progNext,
};

interface Row {
  id: string;
  title: string;
  owner: StoryStep["owner"];
  ownerLabel?: string;
  state: RowState;
  when: string;
  /** The when is past its date: shown in red. */
  overdue: boolean;
  lines: string[];
  /** The line that waits on others, which opens to name the items holding the step up. */
  waiting: string;
  held: AmazonNeed[];
}

/** One step of the chapter on ``b``: null for a step no building's record answers. */
const rowOf = (
  b: AmazonBuilding,
  s: StoryStep,
  o: Overrides,
  need: Record<string, Date> | null,
  now: Date,
): Row | null => {
  const shown = shownState(b, s.id);
  if (shown === null) return null;
  const step = b.steps[s.id];
  const by = need?.[s.id] ?? null;
  const late =
    shown === "open" &&
    (s.id === "inputs" ? lateInputs(b, need, now).length > 0 : by !== null && by < now);
  const state: RowState =
    shown === "todo"
      ? step?.state === "not_tracked"
        ? "notYet"
        : "notStarted"
      : late
        ? "late"
        : shown;
  let when = "";
  let overdue = false;
  if (step && shown === "done") {
    when = step.date ? formatDate(day(step.date)) : "Date not on record";
  } else if (step?.due) {
    const due = day(step.due);
    overdue = due < now;
    when = `${step.estimated ? "Our estimate" : overdue ? "Was due" : "Due"} ${formatDate(due)}`;
  } else if (step && by && !isSettled(b, s.id)) {
    overdue = by < now;
    when = `Needed by ${formatDate(by)}${overdue ? `, ${daysBetween(by, now)} days ago` : ""}`;
  }
  const freehold = b.freehold && s.id === "llsignoff";
  const lines: string[] = [];
  let waiting = "";
  if (step) {
    const cell = shownCell(b, s.id, o, need);
    const record = shownLine(b, s.id, o);
    const note = capitalised(step.note ?? "");
    const held = step.state === "blocked" ? waitingText(b, s.id) : "";
    waiting = held;
    // Each line only where it says something the state, the date or a line above does not.
    for (const line of [held, cell.status, record, note]) {
      if (line && !repeatsBold(line, [ROW_LABEL[state], when, ...lines].join(" "))) {
        lines.push(line);
      }
    }
  }
  return {
    id: s.id,
    title: freehold ? "Amazon Real Estate approval" : s.title,
    owner: freehold ? "amazon" : s.owner,
    ownerLabel: freehold ? undefined : s.ownerLabel,
    state,
    when,
    overdue,
    lines,
    waiting,
    held: waiting ? heldItems(b, s.id) : [],
  };
};

export const ChapterPanel = ({
  b,
  steps,
  o,
  need,
  now,
  worked,
}: {
  b: AmazonBuilding;
  steps: StoryStep[];
  o: Overrides;
  need: Record<string, Date> | null;
  now: Date;
  /** What edit mode opens for a figure the page works out. */
  worked: (label: string) => Target;
}) => {
  const [open, setOpen] = useState<number | null>(null);
  const chapter = open === null ? null : CHAPTERS[open];
  const rows =
    open === null
      ? []
      : steps
          .filter((s) => s.chapter === open && !s.hidden)
          .map((s) => rowOf(b, s, o, need, now))
          .filter((r): r is Row => r !== null);
  const progress = open === null ? null : chapterProgress(b, steps, open);
  const under = rows.filter((r) => ["open", "blocked", "late"].includes(r.state)).length;
  return (
    <>
      <div className={styles.chapterStrip}>
        {CHAPTERS.map((c, i) => {
          const p = chapterProgress(b, steps, i);
          const isOpen = open === i;
          return (
            <button
              key={c.title}
              type="button"
              className={`${TILE_CLASS[p.state]}${isOpen ? ` ${styles.chapterOpen}` : ""}`}
              aria-expanded={isOpen}
              aria-controls="chapter-panel"
              onClick={() => setOpen(isOpen ? null : i)}
            >
              <span className={styles.chapterTop}>
                <span>
                  {i + 1} · {c.title}
                </span>
                <Chevron open={isOpen} />
              </span>
              <small>
                {p.done}/{p.total} done
              </small>
            </button>
          );
        })}
      </div>
      {chapter && progress && open !== null && (
        <section id="chapter-panel" className={styles.chapterPanel} aria-label={chapter.title}>
          <div className={styles.chapterPanelHead}>
            <div>
              <p className={styles.chapterPanelTitle}>
                {open + 1} · {chapter.title}
              </p>
              <p className={styles.chapterPanelLine}>{chapter.line}</p>
            </div>
            <span className={styles.chapterPanelCount}>
              <Editable target={worked(`Chapter ${open + 1} progress`)} keys={[]}>
                {progress.done} of {progress.total} done
              </Editable>
              {under ? ` · ${under} under way` : ""}
            </span>
          </div>
          {rows.map((r) => (
            <div key={r.id} className={styles.chapterRow}>
              <span className={`${styles.rowPill} ${ROW_CLASS[r.state]}`}>{ROW_LABEL[r.state]}</span>
              <span className={styles.chapterRowTitle}>
                {r.title} <PartyChip owner={r.owner} label={r.ownerLabel} />
              </span>
              <span className={r.overdue ? `${styles.chapterRowWhen} ${styles.late}` : styles.chapterRowWhen}>
                {r.when}
              </span>
              {r.lines.length > 0 && (
                <div className={styles.chapterRowLines}>
                  {r.lines.map((l) =>
                    l === r.waiting ? (
                      <HeldItems key={l} line={l} held={r.held} />
                    ) : (
                      <p key={l}>{l}</p>
                    ),
                  )}
                </div>
              )}
            </div>
          ))}
        </section>
      )}
    </>
  );
};
